import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Header } from './Header/Header';
import { Sidebar, SidebarItem } from './Sidebar/Sidebar';
import { useAppDispatch } from '@/hooks';
import { logout } from '@/store/slices/authSlice';

export default function MainLayout() {
  const [sidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Define navigation items
  const sidebarItems: SidebarItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'LayoutDashboard',
      href: '/dashboard',
    },
    {
      id: 'product-inventory',
      label: 'Product Inventory',
      icon: 'Package',
      href: '/product-inventory',
    },
    {
      id: 'product-prices',
      label: 'Product Pricing',
      icon: 'IndianRupee',
      href: '/products/prices',
    },
    {
      id: 'purchase-orders',
      label: 'Purchase Orders',
      icon: 'ShoppingBag',
      href: '/purchase-orders',
    },
    {
      id: 'grns',
      label: 'GRNs',
      icon: 'FileText',
      href: '/grns',
    },
    {
      id: 'batches',
      label: 'Batches',
      icon: 'Box',
      href: '/batches',
    },
    {
      id: 'warehouses',
      label: 'Warehouses',
      icon: 'Warehouse',
      href: '/warehouses',
    },
    {
      id: 'sales',
      label: 'Sales',
      icon: 'ShoppingCart',
      href: '/sales',
    },
    {
      id: 'returns',
      label: 'Returns',
      icon: 'RefreshCw',
      href: '/returns',
    },
    {
      id: 'discounts',
      label: 'Discounts',
      icon: 'Percent',
      href: '/discounts',
    },
    {
      id: 'collaborators',
      label: 'Collaborators',
      icon: 'Users',
      href: '/collaborators',
    },
    {
      id: 'categories',
      label: 'Categories',
      icon: 'Folder',
      href: '/categories',
    },
    {
      id: 'subcategories',
      label: 'Subcategories',
      icon: 'FolderTree',
      href: '/subcategories',
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: 'BarChart3',
      href: '/reports',
    },
  ];

  // Determine active navigation
  const activeId = sidebarItems.find((item) => location.pathname.startsWith(item.href || ''))?.id;

  // Remove navItems from header - navigation is handled by sidebar only
  const navItems: Array<{ label: string; href: string; active?: boolean }> = [];

  const handleUserMenuClick = (action: string) => {
    if (action === 'logout') {
      dispatch(logout());
      navigate('/login');
    } else if (action === 'profile') {
      navigate('/profile');
    } else if (action === 'settings') {
      navigate('/settings');
    }
  };

  const handleSidebarItemClick = (item: SidebarItem) => {
    if (item.href) {
      navigate(item.href);
      setMobileMenuOpen(false);
    }
  };

  const handleNewSaleClick = () => {
    // If already on create sales page, dispatch event to add new tab
    // Otherwise, navigate to the page
    if (location.pathname === '/sales/create') {
      window.dispatchEvent(new Event('add-sales-tab'));
    } else {
      navigate('/sales/create');
    }
  };

  const handleNotificationClick = () => {
    // TODO: Implement notifications panel
  };

  return (
    <div className="min-h-screen bg-background-secondary">
      {/* Header */}
      <Header
        userName="Admin User"
        userRole="Administrator"
        notificationCount={3}
        navItems={navItems}
        onUserMenuClick={handleUserMenuClick}
        onNotificationClick={handleNotificationClick}
        onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        onNewSaleClick={handleNewSaleClick}
      />

      <div className="flex h-[calc(100vh-64px)] mt-16">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block">
          <Sidebar
            items={sidebarItems}
            activeId={activeId}
            collapsed={sidebarCollapsed}
            onItemClick={handleSidebarItemClick}
          />
        </aside>

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-neutral-900 bg-opacity-50 z-20 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />
            <aside className="fixed top-16 left-0 bottom-0 z-30 lg:hidden">
              <Sidebar
                items={sidebarItems}
                activeId={activeId}
                collapsed={false}
                onItemClick={handleSidebarItemClick}
              />
            </aside>
          </>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
